import React, { useState, useEffect, useRef, useCallback } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import LoginPage from './components/LoginPage'
import ResetPasswordPage from './components/ResetPasswordPage'
import VerifyEmailPage from './components/VerifyEmailPage'
import EmailChangePage from './components/EmailChangePage'
import ConsentDocumentPage from './components/ConsentDocumentPage'
import ConsentGate from './components/ConsentGate'
import Dashboard from './components/Dashboard'
import ConnectorGallery from './components/ConnectorGallery'
import ConnectorSetup from './components/ConnectorSetup'
import ScenarioOverview from './components/ScenarioOverview'
import HistoryExportEditor from './components/HistoryExportEditor'
import ReferenceMappingEditor from './components/ReferenceMappingEditor'
import ConnectorConnect from './components/ConnectorConnect'
import IntegrationLogs from './components/IntegrationLogs'
import FoundationPage from './components/FoundationPage'
import { api, CONSENT_REQUIRED_EVENT } from './api/client'
import { ROUTES } from './routes'
import { GalleryPreloadContext } from './GalleryPreloadContext'
import { initSupportChat, identifySupportChatUser } from './supportChat'

export function RequireAuth ({ user, setUser, onLogout = () => setUser(null) }) {
  const location = useLocation()
  if (!user) return <Navigate to={ROUTES.LOGIN} replace state={{ from: location }} />
  // Экран согласия встаёт на место любой защищённой страницы без навигации:
  // после «Продолжить» пользователь оказывается там, куда шёл, включая
  // marketplace claim в hash.
  if (user.consent_required) {
    return <ConsentGate user={user} onAccepted={setUser} onLogout={onLogout} />
  }
  return <Outlet />
}

export function LoginRoute ({ user, setUser }) {
  const location = useLocation()
  const destination = location.state?.from || ROUTES.INTEGRATIONS
  return user
    ? <Navigate to={destination} replace />
    : <LoginPage onLogin={setUser} />
}

export function promoteMarketplaceClaimToTopWindow (browserWindow = window) {
  const claimToken = new URLSearchParams(browserWindow.location.hash.replace(/^#/, '')).get('marketplace_claim')
  if (!claimToken || browserWindow.self === browserWindow.top) return false

  try {
    browserWindow.top.location.replace(browserWindow.location.href)
    return true
  } catch {
    return false
  }
}

export default function App () {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [galleryPreload, setGalleryPreload] = useState(null)

  // Предзагрузка принадлежит аккаунту, для которого запрошена. Номер
  // поколения растёт при выходе: ответ, запрошенный для прежнего аккаунта и
  // пришедший позже, отбрасывается — иначе следующий аккаунт в той же вкладке
  // видел бы чужие подключения в галерее и в чате поддержки.
  const preloadGeneration = useRef(0)
  const preloadRequested = useRef(false)

  const loadGalleryPreload = useCallback(() => {
    const generation = preloadGeneration.current
    preloadRequested.current = true
    Promise.all([api.getConnectors(), api.getConnections()])
      .then(([c, cn]) => {
        if (generation !== preloadGeneration.current) return
        setGalleryPreload({
          connectors: c.connectors,
          connections: cn.connections
        })
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    // Пробуем выйти из third-party iframe Bitrix24 в обычное окно платформы, где
    // доступна её first-party сессия. Только best effort и никогда не преграда
    // отрисовке: браузер блокирует навигацию верхнего окна из cross-origin iframe
    // молча, без исключения, — прежний ранний return оставлял приложение в
    // «Загрузка…» навсегда, и слайдер Bitrix24 выглядел неоткрывающимся.
    // Если навигация всё же прошла, страница заменяется и лишние запросы
    // отменяются вместе с документом.
    promoteMarketplaceClaimToTopWindow()

    const token = localStorage.getItem('ch_token')
    if (!token) { setLoading(false); return }

    // Параллельно с /auth/me, чтобы галерея не ждала профиль.
    loadGalleryPreload()

    api.me()
      .then(data => setUser(data.user))
      .catch(() => localStorage.removeItem('ch_token'))
      .finally(() => setLoading(false))
  }, [loadGalleryPreload])

  // Вход без перезагрузки страницы (после «Выйти» или на экране логина):
  // предзагрузка запрашивается уже для нового аккаунта.
  useEffect(() => {
    if (user && !preloadRequested.current) loadGalleryPreload()
  }, [user, loadGalleryPreload])

  const handleLogout = useCallback(() => {
    preloadGeneration.current += 1
    preloadRequested.current = false
    setGalleryPreload(null)
    setUser(null)
  }, [])

  // Сервер закрыл API до согласия (403 CONSENT_REQUIRED) — например, дата
  // документа сменилась при живой сессии. Экран согласия встаёт на место
  // страницы через состояние пользователя, без перезагрузки: перезагрузка
  // зацикливалась, когда 403 приходил от предзагрузки галереи раньше /auth/me.
  useEffect(() => {
    const requireConsent = () => {
      setUser(current => (current && !current.consent_required)
        ? { ...current, consent_required: true }
        : current)
    }
    window.addEventListener(CONSENT_REQUIRED_EVENT, requireConsent)
    return () => window.removeEventListener(CONSENT_REQUIRED_EVENT, requireConsent)
  }, [])

  // Чат техподдержки поднимается один раз на всё приложение и не ждёт входа:
  // с экрана логина тоже нужно уметь позвать поддержку.
  useEffect(() => { initSupportChat() }, [])

  // Профиль и список подключений появляются позже загрузки виджета и меняются
  // при входе под другим аккаунтом — поэтому идентификация привязана к ним,
  // а не к монтированию.
  useEffect(() => {
    identifySupportChatUser(user, galleryPreload?.connections || null)
  }, [user, galleryPreload])

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F1F5F9' }}>
        <div style={{ color: '#64748B', fontSize: 14 }}>Загрузка…</div>
      </div>
    )
  }

  return (
    <GalleryPreloadContext.Provider value={galleryPreload}>
      <BrowserRouter>
        <Routes>
          <Route
            path={ROUTES.LOGIN}
            element={<LoginRoute user={user} setUser={setUser} />}
          />

          {/* Страницы по ссылкам из писем: доступны без авторизации и до
              catch-all, иначе редирект увёл бы с них на галерею. */}
          <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage onLogin={setUser} />} />
          <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
          <Route path={ROUTES.CONFIRM_EMAIL_CHANGE} element={<EmailChangePage action='confirm' />} />
          <Route path={ROUTES.CANCEL_EMAIL_CHANGE} element={<EmailChangePage action='cancel' />} />
          <Route path={ROUTES.POSITION} element={<ConsentDocumentPage />} />

          <Route element={<RequireAuth user={user} setUser={setUser} onLogout={handleLogout} />}>
            <Route path={ROUTES.FOUNDATION} element={<FoundationPage />} />
            <Route element={<Dashboard user={user} setUser={setUser} onLogout={handleLogout} />}>
              <Route path={ROUTES.INTEGRATIONS} element={<ConnectorGallery />} />
              <Route path='/integrations/:slug/connect' element={<ConnectorConnect user={user} />} />
              <Route path='/integrations/:slug/logs' element={<IntegrationLogs user={user} />} />
              <Route path='/integrations/:slug/references/:type' element={<ReferenceMappingEditor user={user} />} />
              <Route path='/integrations/:slug/scenarios/:scenarioId' element={<ScenarioOverview user={user} />} />
              <Route path='/integrations/:slug/exports/:exportId' element={<HistoryExportEditor user={user} />} />
              <Route path='/integrations/:slug' element={<ConnectorSetup />} />
            </Route>
          </Route>

          <Route path='*' element={<Navigate to={ROUTES.INTEGRATIONS} replace />} />
        </Routes>
      </BrowserRouter>
    </GalleryPreloadContext.Provider>
  )
}
